import { useEffect, useMemo, useState } from 'react'
import { api } from '@/services/api'
import { Loader2, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface MarkdownViewerProps {
  orgId: string
  fileId: string
  fileName: string
  onDownload?: () => void
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function renderInline(text: string): string {
  return text
    .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-muted text-[0.9em]">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a class="text-blue-600 underline" href="$2" target="_blank" rel="noreferrer">$1</a>')
}

function markdownToHtml(markdown: string): string {
  const lines = markdown.split(/\r?\n/)
  const html: string[] = []
  let inCode = false
  let inList = false

  for (const raw of lines) {
    const line = escapeHtml(raw)

    if (line.startsWith('```')) {
      if (!inCode) {
        if (inList) {
          html.push('</ul>')
          inList = false
        }
        html.push('<pre class="rounded-md bg-muted p-3 overflow-x-auto"><code>')
        inCode = true
      } else {
        html.push('</code></pre>')
        inCode = false
      }
      continue
    }

    if (inCode) {
      html.push(`${line}\n`)
      continue
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/)
    if (heading) {
      if (inList) {
        html.push('</ul>')
        inList = false
      }
      const level = heading[1].length
      const content = renderInline(heading[2])
      html.push(`<h${level} class="font-semibold mt-4 mb-2">${content}</h${level}>`)
      continue
    }

    const listItem = line.match(/^[-*]\s+(.*)$/)
    if (listItem) {
      if (!inList) {
        html.push('<ul class="list-disc ml-6 my-2 space-y-1">')
        inList = true
      }
      html.push(`<li>${renderInline(listItem[1])}</li>`)
      continue
    }

    if (inList) {
      html.push('</ul>')
      inList = false
    }

    if (line.trim() === '') {
      html.push('<div class="h-3"></div>')
      continue
    }

    html.push(`<p class="leading-7">${renderInline(line)}</p>`)
  }

  if (inList) html.push('</ul>')
  if (inCode) html.push('</code></pre>')

  return html.join('')
}

export function MarkdownViewer({ orgId, fileId, fileName, onDownload }: MarkdownViewerProps) {
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const blob = await api.files.downloadBlob(orgId, fileId, { inline: true })
        const text = await blob.text()
        if (!mounted) return
        setContent(text)
      } catch (e) {
        if (!mounted) return
        setError((e as Error).message || 'Failed to load markdown')
      } finally {
        if (mounted) setLoading(false)
      }
    }

    load()
    return () => {
      mounted = false
    }
  }, [orgId, fileId])

  const renderedHtml = useMemo(() => markdownToHtml(content), [content])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading markdown...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" onClick={onDownload}>
          <Download className="h-4 w-4 mr-2" />
          Download Instead
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-2 border-b bg-muted/50">
        <span className="text-sm font-medium truncate flex-1 mr-4">{fileName}</span>
        <Button variant="ghost" size="sm" onClick={onDownload}>
          <Download className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-6">
        <article className="max-w-none text-sm space-y-2" dangerouslySetInnerHTML={{ __html: renderedHtml }} />
      </div>
    </div>
  )
}
